import CustomLoader from "@/components/CustomLoader/customLoader.component";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import InboxFilters from "@/components/InboxView/InboxFilters/inboxFilters.component";
import SingleLineNotification from "@/components/SingleLineNotification/singleLineNotification.component";
import SingleTaskViewSheet from "@/components/SingleTaskViewSheet/singleTaskViewSheet.component";
import { ROUTE_INBOX } from "@/constants/routes.constants";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { useUser } from "@/hooks/AppContext/user.hook";
import { fetchNotifications } from "@/models/notifications.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shadcn/components/ui/resizable";
import { NewTaskTag, UserNotification } from "@/types";
import {
  copyToClipboard,
  getNewTaskTagByID,
  getNumArrayFromString,
} from "@/utils/utils";
import { Braces } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import Swal from "sweetalert2";

const VISIBLE_NOTIFICATIONS = 50; // default number of notifications to show

const InboxView: React.FC = () => {
  const { getUserFullNameByID } = useUser();
  const { userTags, precacheTasks } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<
    UserNotification[]
  >([]);
  const [showUnread, setShowUnread] = useState<boolean>(true);
  const [selectedSenders, setSelectedSenders] = useState<number[]>([]);
  const [visibleNotifications, setVisibleNotifications] = useState<number>(
    VISIBLE_NOTIFICATIONS
  );

  const { taskID } = useParams();
  const nav = useNavigate();

  const [viewingTaskID, setViewingTaskID] = useState<number | null>(null);
  const controllerRef = useRef(new AbortController());
  const isNotSmall = useMediaQuery("sm");

  useEffect(() => {
    // console.log("Task ID", taskID);
    if (!taskID) {
      setViewingTaskID(null);
      return;
    }
    // check if number
    const taskIDNum = Number(taskID);
    if (isNaN(taskIDNum)) {
      console.error("Task ID is not a number");
      Swal.fire({
        title: "Error",
        text: "Task ID is not a number",
        icon: "error",
      });
      setViewingTaskID(null);
      return;
    }
    setViewingTaskID(taskIDNum);
  }, [taskID]);

  useEffect(() => {
    async function getNotifications() {
      controllerRef.current.abort("Aborted - Refetching notifications");
      // Create a new AbortController
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      setLoading(true);
      setError(null);
      setNotifications([]);
      const response = await fetchNotifications({
        showUnread,
        signal,
      });

      if (signal.aborted) {
        return;
      }

      if (response.rStatus === "error") {
        console.log(response);
        setError(response.message);
      } else {
        const orderedNotifications = response.notifications.sort(
          (a, b) =>
            new Date(b.notification_date).getTime() -
            new Date(a.notification_date).getTime()
        );

        setNotifications(orderedNotifications);
        setVisibleNotifications(VISIBLE_NOTIFICATIONS);
      }
      setLoading(false);
    }
    getNotifications();

    return () => {
      controllerRef.current.abort("Cleanup - InboxView unmounted");
    };
  }, [showUnread]);

  useEffect(() => {
    if (!taskID && isNotSmall) {
      if (notifications.length > 0) {
        nav(`${ROUTE_INBOX}/${notifications[0].task}`);
      }
    }
  }, [notifications, taskID, nav, isNotSmall]);

  function removeNotification(notificationID: number) {
    setNotifications((prevNotifications) =>
      prevNotifications.filter(
        (notification) => notification.id !== notificationID
      )
    );
  }

  useEffect(() => {
    if (selectedSenders.length > 0) {
      const filtered = notifications.filter((notification) =>
        selectedSenders.includes(notification.sender)
      );
      setFilteredNotifications(filtered);
    } else {
      setFilteredNotifications(notifications);
    }
  }, [selectedSenders, notifications]);

  function getTagsFromIDString(str: string): NewTaskTag[] {
    // getNewTaskTagByID(tag.id, userTags);
    return getNumArrayFromString(str)
      .map((tagID) => getNewTaskTagByID(tagID, userTags))
      .filter((tag): tag is NewTaskTag => tag !== undefined);
  }

  function handleCopyNotificationsToClipboard() {
    try {
      if (!filteredNotifications || filteredNotifications.length === 0) {
        return;
      }

      const adjustedNotifications = filteredNotifications.map((notification) =>
        prepareNotificationForCopy(notification)
      );

      copyToClipboard(
        "Notifications",
        JSON.stringify(adjustedNotifications, null, 2).replace(/\\"/g, "")
      );
    } catch (error) {
      console.error("Error copying notifications to clipboard", error);
      toast.error("Error copying notifications to clipboard", {
        description: "Please try again later.",
      });
    }
  }

  function prepareNotificationForCopy(notification: UserNotification) {
    const task = precacheTasks
      ? precacheTasks.find((task) => task.id === notification.task)
      : undefined;
    return {
      id: notification.id,
      date: notification.notification_date,
      status: notification.status === "Complete" ? "Read" : "Unread",
      sender: {
        id: notification.sender,
        name: getUserFullNameByID(notification.sender),
      },
      task: task
        ? {
            taskID: notification.task,
            taskName: task.title,
          }
        : `Task ID ${notification.task} not found`,
      activity: notification.activity.map((act) => {
        switch (act.field) {
          case "due_date":
            return `Change the due date to ${act.value || "No Date"}`;
          case "status":
            return `Marked task ${act.value}`;
          case "assigned_to":
            return `Assigned to ${getUserFullNameByID(act.value)}`;
          case "collaborators": {
            const collaborators = getNumArrayFromString(act.value).map(
              (collaborator) => {
                return getUserFullNameByID(collaborator);
              }
            );
            return `Updated collaborators to ${collaborators.join(", ")}`;
          }
          case "tags": {
            const tags = getTagsFromIDString(act.value).map((tag) => {
              return tag.tag;
            });
            return `Updated Tags to ${tags.join(", ")}`;
          }
          case "description":
            return `Updated Description to ${act.value}`;
          case "parent_id": {
            const parentTask = precacheTasks?.find(
              (task) => task.id === Number(act.value)
            );
            if (parentTask) {
              return `Updated parent task to ${parentTask.title}`;
            } else {
              return `Updated parent task to ${act.value}`;
            }
          }
          case "title":
            return `Updated title to ${act.value}`;

          default:
            return `${act.field}: ${act.value}`;
        }
      }),
    };
  }

  function loadMoreNotifications() {
    setVisibleNotifications((prev) => prev + VISIBLE_NOTIFICATIONS);
  }

  return (
    <ResizablePanelGroup
      direction="horizontal"
      className="flex flex-col h-full relative"
      id={"panel-group-inbox-view"}
    >
      <ResizablePanel
        id={"panel-inbox-view"}
        order={1}
        className="h-full flex flex-col"
      >
        {/* toggle read  */}
        <div>
          <h1 className="text-3xl font-bold text-center text-primary pt-3">
            Inbox
          </h1>
        </div>
        <div className="flex flex-row justify-between items-center gap-2 mt-0 m-3 flex-wrap">
          <div className="">
            <InboxFilters
              selectedSenders={selectedSenders}
              setSelectedSenders={setSelectedSenders}
            />
          </div>
          <div className="flex justify-end gap-3">
            {isNotSmall && (
              <Button
                variant={"outline"}
                onClick={handleCopyNotificationsToClipboard}
                className={`px-2 py-1 rounded-md`}
              >
                <Braces />
                Copy
              </Button>
            )}
            <Button
              onClick={() => setShowUnread(!showUnread)}
              className={`px-2 py-1 rounded-md`}
            >
              {showUnread ? "Show Archived" : "Show Unread"}
            </Button>
          </div>
        </div>
        {loading ? (
          <CustomLoader />
        ) : error ? (
          <div>Error: {error}</div>
        ) : (
          <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
            {notifications.length == 0 ? (
              <div className="text-center text-lg font-bold mt-3">
                You're all caught up!
              </div>
            ) : filteredNotifications.length == 0 ? (
              <div className="text-center text-lg font-bold mt-3">
                No notifications found
              </div>
            ) : (
              <>
                {filteredNotifications
                  .slice(0, visibleNotifications)
                  .map((notification) => (
                    <ErrorBoundary key={notification.id}>
                      <SingleLineNotification
                        key={notification.id}
                        notification={notification}
                        removeNotification={removeNotification}
                      />
                    </ErrorBoundary>
                  ))}
                {visibleNotifications < filteredNotifications.length && (
                  <div className="text-center my-4">
                    <Button
                      onClick={loadMoreNotifications}
                      className="px-4 py-2 rounded-md"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </ResizablePanel>
      {viewingTaskID && (
        <SingleTaskViewSheet taskID={viewingTaskID} disableClose={isNotSmall} />
      )}
    </ResizablePanelGroup>
  );
};

export default InboxView;
